// chatbot-firebase.js — ES module, logs only on first real message
// IMPORTANT: load with <script type="module" src="{{ 'chatbot-firebase.js' | asset_url }}"></script>

import { initializeApp, getApps, getApp } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-app.js";
import { getAnalytics } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-analytics.js";
import { getAuth, signInAnonymously, onAuthStateChanged } from "https://www.gstatic.com/firebasejs/11.3.1/firebase-auth.js";
import {
  getFirestore, doc, collection, serverTimestamp,
  writeBatch, increment
} from "https://www.gstatic.com/firebasejs/11.3.1/firebase-firestore.js";

if (!window.__AQ_FB_INIT__) {
  window.__AQ_FB_INIT__ = true;

  // public flags
  window.chatSessionStarted = false;
  window.__AQ_FIREBASE_READY__ = false;

  let currentSessionId = null;

  // --- Firebase config ---
  const firebaseConfig = {
    apiKey: "AIzaSyBoiOBri3L_U39jgHDj2PdePW4u1KNig5o",
    authDomain: "askaq-805c5.firebaseapp.com",
    projectId: "askaq-805c5",
    storageBucket: "askaq-805c5.firebasestorage.app",
    messagingSenderId: "11279561440",
    appId: "1:11279561440:web:85772baeed0bb50d8f9685",
    measurementId: "G-XJTWP50G73"
  };

  // --- Init (safe if already initialized elsewhere) ---
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  // Analytics can fail on some environments; ignore if it does
  try { getAnalytics(app); } catch (_) {}

  const auth = getAuth(app);
  const db   = getFirestore(app);

  // Anonymous auth (once)
  onAuthStateChanged(auth, (user) => {
    if (!user) {
      signInAnonymously(auth).catch((err) => {
        console.error("[chatbot] Anonymous sign-in failed:", err);
      });
    }
  });

  // --- Helpers ---
  const pad = (n) => String(n).padStart(2, "0");
  const now = () => new Date();
  const isNonEmpty = (t) => typeof t === "string" && t.trim().length > 0;

  const generateSessionId = () => {
    const d = now();
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };
  const timePrefix = () => {
    const d = now();
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // --- Core: create session + first message atomically ---
  async function createSessionWithFirstInteraction(role, message) {
    const text = (message ?? "").trim();
    if (!isNonEmpty(text)) return null;

    const sessionId  = generateSessionId();
    const sessionRef = doc(db, "sessions", sessionId);

    const autoId     = doc(collection(db, "sessions", sessionId, "messages")).id;
    const messageId  = `${timePrefix()}-${autoId}`;
    const messageRef = doc(db, "sessions", sessionId, "messages", messageId);

    const ts = serverTimestamp();

    const batch = writeBatch(db);
    batch.set(sessionRef, {
      startTime: ts,
      hasInteraction: true,
      firstMessageAt: ts,
      lastMessageAt: ts,
      lastMessagePreview: text.slice(0, 200),
      messageCount: 1
    });
    batch.set(messageRef, {
      role: role || "user",
      message: text,
      timestamp: ts
    });

    await batch.commit();
    currentSessionId = sessionId;
    window.chatSessionStarted = true;
    return sessionId;
  }

  // --- Append follow-ups ---
  async function appendInteraction(role, message) {
    const text = (message ?? "").trim();
    if (!isNonEmpty(text)) return;

    if (!currentSessionId) {
      await createSessionWithFirstInteraction(role, text);
      return;
    }

    const sessionRef = doc(db, "sessions", currentSessionId);
    const autoId     = doc(collection(db, "sessions", currentSessionId, "messages")).id;
    const msgId      = `${timePrefix()}-${autoId}`;
    const messageRef = doc(db, "sessions", currentSessionId, "messages", msgId);

    const batch = writeBatch(db);
    batch.set(messageRef, {
      role: role || "user",
      message: text,
      timestamp: serverTimestamp()
    });
    batch.set(sessionRef, {
      hasInteraction: true,
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: text.slice(0, 200),
      messageCount: increment(1)
    }, { merge: true });

    await batch.commit();
  }

  // --- Public API used by chatbot-core.js ---
  async function logInteraction(role, message) {
    if (!currentSessionId) {
      await createSessionWithFirstInteraction(role, message);
    } else {
      await appendInteraction(role, message);
    }
  }
  async function startSession() {
    console.warn("[chatbot] startSession() is deprecated. Sessions are created on first non-empty message.");
  }

  window.logInteraction = logInteraction;
  window.startSession   = startSession;

  window.__AQ_FIREBASE_READY__ = true;
  console.debug("[chatbot] Firebase ready");
}
